import {
  clampPointToBounds,
  DragBoundaryRect,
  JUST_IN_TIME_CALL_DRAG_Z_INDEX,
  JUST_IN_TIME_CALL_Z_INDEX,
} from '../justInTimeCallDragBounds.util';
import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';

/** Measured card dimensions for drag clamping. */
export interface BoundedDragCardSize {
  width: number;
  height: number;
}

/** Props spread onto the drag handle element. */
export interface BoundedPointerDragHandleProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  isDragging: boolean;
}

/** Fixed-position style for the draggable container. */
export interface BoundedPointerDragContainerStyle {
  left: number | undefined;
  top: number | undefined;
  zIndex: number;
}

/** Options for {@link useBoundedPointerDrag}. */
export interface UseBoundedPointerDragOptions {
  bounds: DragBoundaryRect;
  position: { x: number; y: number } | null;
  onPositionChange: (position: { x: number; y: number }) => void;
  cardSize: BoundedDragCardSize;
  enabled?: boolean;
  baseZIndex?: number;
  dragZIndex?: number;
}

/** Return value for {@link useBoundedPointerDrag}. */
export interface UseBoundedPointerDragResult {
  isDragging: boolean;
  dragHandleProps: BoundedPointerDragHandleProps;
  containerPositionStyle: BoundedPointerDragContainerStyle;
}

/**
 * Pointer-driven drag with clamping inside a boundary rectangle.
 * Supports mouse and touch via the Pointer Events API.
 *
 * @param options - Bounds, position state, card size, and z-index overrides.
 * @returns Drag handle props and container positioning style.
 */
export function useBoundedPointerDrag({
  bounds,
  position,
  onPositionChange,
  cardSize,
  enabled = true,
  baseZIndex = JUST_IN_TIME_CALL_Z_INDEX,
  dragZIndex = JUST_IN_TIME_CALL_DRAG_Z_INDEX,
}: UseBoundedPointerDragOptions): UseBoundedPointerDragResult {
  const [isDragging, setIsDragging] = useState(false);
  const dragSessionRef = useRef<{
    pointerId: number;
    startPointerX: number;
    startPointerY: number;
    startPositionX: number;
    startPositionY: number;
  } | null>(null);
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    const currentPosition = positionRef.current;
    if (!currentPosition) {
      return;
    }

    // Without a measured size we can still pull a stale top back into the
    // rect. Leaving y at/past bounds.bottom with max-height:0 traps the card.
    if (cardSize.width <= 0 || cardSize.height <= 0) {
      const nextX = Math.min(
        Math.max(currentPosition.x, bounds.left),
        Math.max(bounds.left, bounds.right)
      );
      const nextY =
        currentPosition.y >= bounds.bottom || currentPosition.y < bounds.top
          ? bounds.top
          : currentPosition.y;

      if (nextX !== currentPosition.x || nextY !== currentPosition.y) {
        onPositionChange({ x: nextX, y: nextY });
      }
      return;
    }

    const clamped = clampPointToBounds(
      currentPosition.x,
      currentPosition.y,
      cardSize.width,
      cardSize.height,
      bounds
    );

    if (
      clamped.x !== currentPosition.x ||
      clamped.y !== currentPosition.y
    ) {
      onPositionChange(clamped);
    }
  }, [bounds, cardSize.height, cardSize.width, onPositionChange]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const session = dragSessionRef.current;
      if (!session || event.pointerId !== session.pointerId || !enabled) {
        return;
      }

      const deltaX = event.clientX - session.startPointerX;
      const deltaY = event.clientY - session.startPointerY;
      const nextX = session.startPositionX + deltaX;
      const nextY = session.startPositionY + deltaY;

      onPositionChange(
        clampPointToBounds(
          nextX,
          nextY,
          cardSize.width,
          cardSize.height,
          bounds
        )
      );
    },
    [bounds, cardSize.height, cardSize.width, enabled, onPositionChange]
  );

  const endDrag = useCallback((event: PointerEvent) => {
    const session = dragSessionRef.current;
    if (!session || event.pointerId !== session.pointerId) {
      return;
    }

    dragSessionRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [endDrag, handlePointerMove, isDragging]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || !position || event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      dragSessionRef.current = {
        pointerId: event.pointerId,
        startPointerX: event.clientX,
        startPointerY: event.clientY,
        startPositionX: position.x,
        startPositionY: position.y,
      };

      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDragging(true);
    },
    [enabled, position]
  );

  return {
    isDragging,
    dragHandleProps: {
      onPointerDown,
      isDragging,
    },
    containerPositionStyle: {
      left: position?.x,
      top: position?.y,
      zIndex: isDragging ? dragZIndex : baseZIndex,
    },
  };
}
